import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Copy, Mail } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetLink, setResetLink] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      toast.success("If the email exists, a reset link has been sent");
      if (data.reset_link) setResetLink(data.reset_link);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(resetLink);
    toast.success("Link copied");
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md bp-card rounded-2xl p-8">
        <div className="overline mb-2">Password recovery</div>
        <h1 className="font-heading text-3xl text-white font-light tracking-tight mb-6">
          Forgot your password?
        </h1>
        <p className="text-sm text-gray-400 mb-6">
          Enter your email and we'll send you a reset link.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-gray-300">Email</Label>
            <Input
              data-testid="forgot-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 bg-[#050914] border-white/10 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]"
            />
          </div>
          <Button type="submit" disabled={busy} className="w-full btn-gold rounded-full h-11" data-testid="forgot-submit">
            {busy ? "Sending..." : "Send reset link"}
          </Button>
        </form>

        {resetLink && (
          <div className="mt-6 p-3 rounded-lg bg-[#D4AF37]/5 border border-[#D4AF37]/30">
            <div className="flex items-center gap-2 text-[11px] text-[#D4AF37] mb-2">
              <Mail className="w-3 h-3" />
              <span className="overline text-[10px]">Demo reset link</span>
            </div>
            <div className="text-[11px] text-gray-400 break-all font-mono mb-2">{resetLink}</div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="rounded-full flex-1 h-8 text-[11px]" onClick={copyLink} data-testid="copy-link-btn">
                <Copy className="w-3 h-3 mr-1" /> Copy
              </Button>
              <Link to={(() => {
                try {
                  const url = new URL(resetLink);
                  return url.pathname + url.search;
                } catch {
                  return resetLink.replace(window.location.origin, "");
                }
              })()} className="flex-1">
                <Button size="sm" className="btn-gold rounded-full w-full h-8 text-[11px]" data-testid="open-link-btn">
                  Open
                </Button>
              </Link>
            </div>
          </div>
        )}

        <div className="text-center text-sm text-gray-400 mt-6">
          Remember it?{" "}
          <Link to="/login" className="text-[#D4AF37] hover:underline">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
